import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  H5_MOBILE_DATE_PICKER_LAYOUT,
  h5MobileDatePickerCssVars,
} from "./H5MobileDatePickerConfig";
import "./H5MobileDatePicker.css";

type PickerItem = {
  value: number;
  label: string;
};

type PickerColumnProps = {
  items: PickerItem[];
  value: number;
  onChange: (value: number) => void;
  active?: boolean;
};

function buildYears(min: Dayjs, max: Dayjs): PickerItem[] {
  const list: PickerItem[] = [];
  for (let y = min.year(); y <= max.year(); y += 1) {
    list.push({ value: y, label: `${y}年` });
  }
  return list;
}

function buildMonths(year: number, min: Dayjs, max: Dayjs): PickerItem[] {
  let start = 1;
  let end = 12;
  if (year === min.year()) {
    start = min.month() + 1;
  }
  if (year === max.year()) {
    end = max.month() + 1;
  }
  const list: PickerItem[] = [];
  for (let m = start; m <= end; m += 1) {
    list.push({ value: m, label: `${m}月` });
  }
  return list;
}

function buildDays(year: number, month: number, min: Dayjs, max: Dayjs): PickerItem[] {
  const monthStart = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);
  const daysInMonth = monthStart.daysInMonth();
  let start = 1;
  let end = daysInMonth;
  if (monthStart.isSame(min, "month")) {
    start = min.date();
  }
  if (monthStart.isSame(max, "month")) {
    end = max.date();
  }
  const list: PickerItem[] = [];
  for (let d = start; d <= end; d += 1) {
    list.push({ value: d, label: `${d}日` });
  }
  return list;
}

function clampPart(value: number, items: PickerItem[]): number {
  if (!items.length) {
    return value;
  }
  if (items.some((item) => item.value === value)) {
    return value;
  }
  return items[items.length - 1].value;
}

function PickerColumn(props: PickerColumnProps) {
  const { items, value, onChange, active = false } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollEndTimerRef = useRef<number | null>(null);
  const itemHeight = H5_MOBILE_DATE_PICKER_LAYOUT.itemHeight;

  const scrollToValue = useCallback(
    (nextValue: number, smooth = false) => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const idx = items.findIndex((item) => item.value === nextValue);
      if (idx < 0) {
        return;
      }
      el.scrollTo({
        top: idx * itemHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    },
    [itemHeight, items]
  );

  useEffect(() => {
    scrollToValue(value);
  }, [items, value, scrollToValue]);

  const snapToNearest = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !items.length) {
      return;
    }
    const idx = Math.max(0, Math.min(Math.round(el.scrollTop / itemHeight), items.length - 1));
    el.scrollTop = idx * itemHeight;
    const next = items[idx]?.value;
    if (next !== undefined && next !== value) {
      onChange(next);
    }
  }, [itemHeight, items, onChange, value]);

  const onScroll = () => {
    if (scrollEndTimerRef.current !== null) {
      window.clearTimeout(scrollEndTimerRef.current);
    }
    scrollEndTimerRef.current = window.setTimeout(() => {
      snapToNearest();
      scrollEndTimerRef.current = null;
    }, 80);
  };

  useEffect(() => {
    return () => {
      if (scrollEndTimerRef.current !== null) {
        window.clearTimeout(scrollEndTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="h5-mobile-date-picker__column">
      <div
        ref={scrollRef}
        className="h5-mobile-date-picker__column-scroll"
        onScroll={onScroll}
        onTouchEnd={snapToNearest}
        onMouseUp={snapToNearest}
      >
        <div className="h5-mobile-date-picker__column-pad" aria-hidden />
        {items.map((item) => (
          <div
            key={item.value}
            className={
              item.value === value && active
                ? "h5-mobile-date-picker__column-item h5-mobile-date-picker__column-item--active"
                : "h5-mobile-date-picker__column-item"
            }
          >
            {item.label}
          </div>
        ))}
        <div className="h5-mobile-date-picker__column-pad" aria-hidden />
      </div>
    </div>
  );
}

export type H5MobileDatePickerProps = {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  placeholder?: string;
  title?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  defaultPickerValue?: Dayjs;
  allowClear?: boolean;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
};

/** 移动端滚轮日期选择：点击唤起底部弹层，年/月/日三列滚动选择 */
export function H5MobileDatePicker(props: H5MobileDatePickerProps) {
  const {
    value,
    onChange,
    placeholder = "请选择日期",
    title = "选择日期",
    minDate = dayjs("1900-01-01"),
    maxDate = dayjs(),
    defaultPickerValue,
    allowClear = false,
    disabled = false,
    className,
    triggerClassName,
  } = props;

  const [open, setOpen] = useState(false);
  const [draftYear, setDraftYear] = useState(maxDate.year());
  const [draftMonth, setDraftMonth] = useState(maxDate.month() + 1);
  const [draftDay, setDraftDay] = useState(maxDate.date());

  const years = useMemo(() => buildYears(minDate, maxDate), [minDate, maxDate]);
  const months = useMemo(
    () => buildMonths(draftYear, minDate, maxDate),
    [draftYear, minDate, maxDate]
  );
  const days = useMemo(
    () => buildDays(draftYear, draftMonth, minDate, maxDate),
    [draftMonth, draftYear, minDate, maxDate]
  );

  const initDraft = useCallback(() => {
    const seed = value ?? defaultPickerValue ?? maxDate;
    const y = clampPart(seed.year(), years);
    const monthItems = buildMonths(y, minDate, maxDate);
    const m = clampPart(seed.month() + 1, monthItems);
    const dayItems = buildDays(y, m, minDate, maxDate);
    const d = clampPart(seed.date(), dayItems);
    setDraftYear(y);
    setDraftMonth(m);
    setDraftDay(d);
  }, [defaultPickerValue, maxDate, minDate, value, years]);

  useEffect(() => {
    if (!open) {
      return;
    }
    initDraft();
  }, [initDraft, open]);

  useEffect(() => {
    const nextMonth = clampPart(draftMonth, months);
    if (nextMonth !== draftMonth) {
      setDraftMonth(nextMonth);
    }
  }, [draftMonth, months]);

  useEffect(() => {
    const nextDay = clampPart(draftDay, days);
    if (nextDay !== draftDay) {
      setDraftDay(nextDay);
    }
  }, [days, draftDay]);

  const onOpen = () => {
    if (disabled) {
      return;
    }
    setOpen(true);
  };

  const onCancel = () => {
    setOpen(false);
  };

  const onConfirm = () => {
    const next = dayjs(
      `${draftYear}-${String(draftMonth).padStart(2, "0")}-${String(draftDay).padStart(2, "0")}`
    );
    if (!next.isValid()) {
      return;
    }
    onChange(next.startOf("day"));
    setOpen(false);
  };

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const onClearInSheet = () => {
    onChange(null);
    setOpen(false);
  };

  const displayText = value ? value.format("YYYY-MM-DD") : placeholder;

  return (
    <div
      className={["h5-mobile-date-picker", className].filter(Boolean).join(" ")}
      style={h5MobileDatePickerCssVars()}
    >
      <button
        type="button"
        className={["h5-mobile-date-picker__trigger", triggerClassName].filter(Boolean).join(" ")}
        disabled={disabled}
        onClick={onOpen}
      >
        <span
          className={
            value
              ? "h5-mobile-date-picker__trigger-text"
              : "h5-mobile-date-picker__trigger-text h5-mobile-date-picker__trigger-text--placeholder"
          }
        >
          {displayText}
        </span>
        {allowClear && value ? (
          <span
            role="button"
            tabIndex={-1}
            className="h5-mobile-date-picker__trigger-clear"
            aria-label="清空"
            onClick={onClear}
          >
            ×
          </span>
        ) : null}
      </button>

      <Drawer
        placement="bottom"
        open={open}
        onClose={onCancel}
        height="auto"
        className="h5-mobile-date-picker__sheet"
        destroyOnClose
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div className="h5-mobile-date-picker__inner">
          <header className="h5-mobile-date-picker__toolbar">
            <div className="h5-mobile-date-picker__toolbar-side h5-mobile-date-picker__toolbar-side--left">
              <button
                type="button"
                className="h5-mobile-date-picker__toolbar-btn h5-mobile-date-picker__toolbar-btn--cancel"
                onClick={onCancel}
              >
                取消
              </button>
            </div>
            <h2 className="h5-mobile-date-picker__toolbar-title">{title}</h2>
            <div className="h5-mobile-date-picker__toolbar-side h5-mobile-date-picker__toolbar-side--right">
              {allowClear ? (
                <button
                  type="button"
                  className="h5-mobile-date-picker__toolbar-btn h5-mobile-date-picker__toolbar-btn--clear"
                  onClick={onClearInSheet}
                >
                  清空
                </button>
              ) : null}
              <button
                type="button"
                className="h5-mobile-date-picker__toolbar-btn h5-mobile-date-picker__toolbar-btn--confirm"
                onClick={onConfirm}
              >
                确定
              </button>
            </div>
          </header>

          <div className="h5-mobile-date-picker__wheels">
            <div className="h5-mobile-date-picker__indicator" aria-hidden />
            <PickerColumn
              items={years}
              value={draftYear}
              onChange={setDraftYear}
              active
            />
            <PickerColumn
              items={months}
              value={draftMonth}
              onChange={setDraftMonth}
              active
            />
            <PickerColumn items={days} value={draftDay} onChange={setDraftDay} active />
          </div>
        </div>
      </Drawer>
    </div>
  );
}
